import { getDateFromString, getDatetimeDiff } from '@/helpers'

const fallbackMeta = { icon: 'mono-notification', color: 'secondary' }

// Icon/color per entity kind a watchlist change can be about (matches
// RevisionDiff::getDiffableAttribute() in saniteta-backend: Brand,
// Presentation, Price, Spc are the only diff_type classes it resolves).
// Reuses the same icons those entities already use elsewhere (route meta
// icons in router/routes.js) so a "Brand changed" notification reads the
// same as the Brand nav/breadcrumb icon does.
const entityTypeMeta = {
  Brand: { icon: 'mono-brand', color: 'primary' },
  Presentation: { icon: 'mono-package', color: 'secondary' },
  Price: { icon: 'mono-coins', color: 'positive' },
  Spc: { icon: 'mono-smpc', color: 'info' }
}

export const getEntityTypeMeta = entityType => entityTypeMeta[entityType] ?? fallbackMeta

// Icon/color per severity for admin-sent notifications (App\Notifications\InAppNotification
// in saniteta-backend). The backend's Filament form picks a severity and now sends it as both
// `type: 'in.app.<severity>'` and `data.type: '<severity>'` — mirrored here via the `in.app.`
// prefix stripped in getNotificationMeta below. Icon shapes (triangle for warning, hexagon for
// error, etc.) match the pre-migration right drawer's severity scheme (see notificationHelpers.js
// on staging). Icons are now the mono- set to match the rest of the migrated inbox UI, which
// dropped the old duo- icon font. Colors deviate from that old scheme per product direction.
const severityMeta = {
  message: { icon: 'mono-megaphone', color: 'info' },
  info: { icon: 'mono-info-circle-solid', color: 'info' },
  success: { icon: 'mono-check-circle-solid', color: 'positive' },
  warning: { icon: 'mono-exclamation-triangle-solid', color: 'warning' },
  error: { icon: 'mono-exclamation-hexagon-solid', color: 'negative' }
}

export const getSeverityMeta = severity => severityMeta[severity] ?? fallbackMeta

const watchlistChangesMeta = { icon: 'mono-watchlist', color: 'info' }

const getNotificationMeta = item => {
  if (item.type === 'watchlist.changes') {
    return watchlistChangesMeta
  }
  // Severity comes from `data.type`, not the `in.app.<severity>` prefix on `type` itself:
  // ~11 pre-existing DB rows still carry the old literal type 'in.app.notification' (not
  // backfilled), which has no real severity to parse out of it — but data.type on those same
  // rows already holds the correct severity string, so reading it directly (as the old
  // pre-migration drawer code did) picks up the right icon/color for them too, for free.
  if (item.type?.startsWith('in.app.')) {
    return getSeverityMeta(item.data?.type)
  }
  return fallbackMeta
}

const parseDate = v => {
  if (!v) return null
  if (v instanceof Date) return v
  return getDateFromString(v)
}

export const enrichNotification = item => {
  const createdAt = parseDate(item.createdAt)
  const readAt = parseDate(item.readAt)
  const meta = getNotificationMeta(item)
  return {
    ...item,
    createdAt,
    readAt,
    icon: meta.icon,
    color: meta.color,
    relativeTimeHuman: createdAt ? getDatetimeDiff(createdAt, new Date()).human : ''
  }
}

/**
 * Per-type row content, keyed by the real `type` values the backend sends
 * (see App\Notifications\* in saniteta-backend) — `watchlist.changes` is the
 * only production type today. `t` is injected rather than imported so this
 * stays a plain, testable function outside the Vue component tree.
 */
export const getNotificationRowContent = (notification, t) => {
  if (notification.type === 'watchlist.changes') {
    const watchlists = notification.data?.watchlists ?? []
    const titles = watchlists.map(w => w.watchlistTitle)
    const extraCount = Math.max(titles.length - 1, 0)
    return {
      title: t('notifications:Types.watchlistChanges.title', { count: notification.data?.changesCount ?? 0 }),
      caption: extraCount > 0
        ? t('notifications:Types.watchlistChanges.caption', { first: titles[0] ?? '', count: extraCount })
        : (titles[0] ?? '')
    }
  }

  return {
    title: notification.data?.title ?? t('notifications:Types.generic.title'),
    caption: notification.data?.caption ?? ''
  }
}

/**
 * Decides what the realtime "new watchlist changes" toast should say and where it should go.
 * A digest can span several watchlists but the user gets ONE toast. Watchlists the user is
 * already viewing are left out (their own "New changes — Refresh" banner covers them); returns
 * null when nothing is left to announce.
 *
 * Target: a lone change goes straight to its page, several changes from one watchlist go to its
 * Changes tab, and a digest across watchlists goes to the list.
 *
 * @param {Array<{ watchlistId: number, watchlistTitle: string, changes?: Array<{ changeId?: number }> }>} watchlists
 * @param {(watchlistId: number) => boolean} isViewing
 */
export const summarizeWatchlistDigest = (watchlists, isViewing = () => false) => {
  const incoming = (watchlists ?? []).filter(({ watchlistId }) => !isViewing(watchlistId))
  if (!incoming.length) return null

  const count = incoming.reduce((sum, { changes }) => sum + (changes?.length ?? 0), 0)
  const [first] = incoming

  if (incoming.length > 1) {
    return { count, watchlists: incoming.length, title: null, target: { name: 'Watchlists' } }
  }

  const changeId = first.changes?.length === 1 ? first.changes[0].changeId : null
  return {
    count,
    watchlists: 1,
    title: first.watchlistTitle,
    target: changeId
      ? { name: 'WatchlistChange', params: { watchlistId: first.watchlistId, changeId } }
      : { name: 'WatchlistChanges', params: { watchlistId: first.watchlistId } }
  }
}
